import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/videos";

const dateStr = () =>
  new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "long", day: "numeric", weekday: "long" }).format(new Date());

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  const navItems = [
    { to: "/", label: "首页" },
    ...categories.map((c) => ({ to: `/category/${c.slug}`, label: c.name })),
    { to: "/about", label: "关于我们" },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      {/* top utility bar */}
      <div className="hidden md:block bg-background-100 border-b border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 flex items-center justify-between h-9 text-xs text-foreground-600">
          <div className="flex items-center gap-3 overflow-hidden">
            <span className="whitespace-nowrap flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {dateStr()}
            </span>
            <span className="text-background-300">|</span>
            <span className="hidden lg:flex items-center gap-2 whitespace-nowrap">
              <span className="text-foreground-500">热搜:</span>
              <span className="flex items-center gap-1 text-secondary-700">
                <i className="ri-fire-line"></i>顶流演唱会后台
              </span>
              <span className="text-secondary-700">老戏骨回归</span>
              <span className="text-secondary-700">爆破戏揭秘</span>
            </span>
          </div>
          <div className="flex items-center gap-4 whitespace-nowrap">
            <Link to="/about#contact" className="hover:text-primary-600 transition-colors">投稿爆料</Link>
            <Link to="/about" className="hover:text-primary-600 transition-colors">联系我们</Link>
          </div>
        </div>
      </div>

      {/* main header */}
      <div className={`mx-auto max-w-7xl px-4 md:px-6 flex items-center gap-4 transition-all ${scrolled ? "py-3" : "py-4"}`}>
        {/* brand */}
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="吃瓜视频网 首页">
          <span className="w-9 h-9 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center text-xl">
            <i className="ri-water-flash-line"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-2xl text-primary-600">吃瓜视频网</span>
            <span className="text-[11px] text-foreground-500 tracking-wide">娱乐八卦 · 一站看全</span>
          </span>
        </Link>

        {/* desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 mx-auto" aria-label="主导航">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                  isActive ? "text-background-50 bg-primary-500 font-bold" : "text-foreground-700 hover:text-primary-600"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* search + cta */}
        <div className="ml-auto flex items-center gap-3">
          <form onSubmit={submitSearch} className="hidden sm:flex items-center gap-0 bg-background-100 border border-background-200 rounded-full overflow-hidden focus-within:border-primary-400">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜明星 / 节目 / 关键词"
              className="bg-transparent px-4 py-2 text-sm outline-none w-44 md:w-52 placeholder:text-foreground-400"
              aria-label="站内搜索"
            />
            <button type="submit" aria-label="搜索" className="px-3 h-full text-primary-600 hover:text-primary-700">
              <i className="ri-search-line text-base"></i>
            </button>
          </form>
          <Link
            to="/about#contact"
            className="hidden lg:inline-flex whitespace-nowrap items-center gap-1.5 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-sm font-bold hover:bg-accent-600 transition-colors"
          >
            <i className="ri-edit-2-line"></i>投稿爆料
          </Link>
          {/* hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-md border border-background-200 flex items-center justify-center text-foreground-700"
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {/* mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2.5 rounded-md text-sm ${isActive ? "bg-primary-500 text-background-50 font-bold" : "text-foreground-700"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <form onSubmit={submitSearch} className="flex items-center gap-2 pt-2">
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜明星 / 节目 / 关键词"
                className="flex-1 bg-background-100 border border-background-200 rounded-full px-4 py-2 text-sm outline-none"
                aria-label="站内搜索"
              />
              <button type="submit" aria-label="搜索" className="w-9 h-9 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
                <i className="ri-search-line"></i>
              </button>
            </form>
          </div>
        </div>
      )}
    </header>
  );
}